/**
 * PwaAccountGate - shows the PWA only to a signed-in restaurant account;
 * otherwise the one shared sign-in screen (PwaLogin).
 *
 * PwaAccountCard - the "signed in as … / Sign out" block every PWA puts in
 * its settings screen.
 */

import React from 'react';
import { LogOut, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PwaLogin from './PwaLogin';
import { useAccount, signOutAccount } from '@/lib/pwaAccount';

export function PwaAccountGate({ appName, icon, accent, subtitle, children }) {
  const account = useAccount();
  if (!account) {
    return <PwaLogin appName={appName} icon={icon} accent={accent} subtitle={subtitle} />;
  }
  return typeof children === 'function' ? children(account) : children;
}

export function PwaAccountCard({ onSignOut, note }) {
  const account = useAccount();
  if (!account) return null;
  const signOut = () => {
    if (onSignOut) onSignOut();
    signOutAccount();
  };
  return (
    <div className="bg-slate-800 rounded-xl p-6" data-testid="pwa-account-card">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Store className="w-5 h-5 text-slate-300" />
        Restaurant account
      </h2>
      <p className="text-white font-medium">{account.restaurant.name || `Restaurant #${account.restaurant.id}`}</p>
      {account.manager?.email && <p className="text-sm text-slate-400">Signed in as {account.manager.email}</p>}
      <p className="text-xs text-slate-500 mt-1">
        {note || 'After signing out, this screen asks for the restaurant sign-in again.'}
      </p>
      <Button variant="outline" onClick={signOut} className="mt-4 border-red-500/40 text-red-300 hover:bg-red-500/10"
        data-testid="pwa-sign-out">
        <LogOut className="w-4 h-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}

export default PwaAccountGate;
